import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Upload, File, Folder, Trash2, Download, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPDocuments = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, docsRes, foldersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/documents/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/documents/documents`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/documents/folders`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setDocuments(docsRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/erp/documents/documents/${docId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      alert('Failed to delete document');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      contract: '📄',
      drawing: '📐',
      invoice: '💰',
      report: '📊',
      certificate: '🏆',
      other: '📁'
    };
    return icons[category] || '📁';
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
          <p className="text-gray-600 mt-1">Upload, organize, and manage project documents</p>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="bg-primary hover:bg-primary/90">
          <Upload className="w-4 h-4 mr-2" />Upload Document
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Documents</p><p className="text-2xl font-bold text-gray-900">{stats?.total_documents || 0}</p></div><File className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Contracts</p><p className="text-2xl font-bold text-purple-600">{stats?.categories?.contract || 0}</p></div><File className="w-10 h-10 text-purple-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Drawings</p><p className="text-2xl font-bold text-green-600">{stats?.categories?.drawing || 0}</p></div><File className="w-10 h-10 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Storage Used</p><p className="text-2xl font-bold text-orange-600">{stats?.total_size_mb || 0}MB</p></div><Folder className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        <Card className="md:col-span-1">
          <div className="p-6">
            <h2 className="text-lg font-bold mb-4">Folders</h2>
            <div className="space-y-2">
              {folders.map(folder => (
                <div key={folder.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <Folder className="w-5 h-5 text-blue-500" />
                  <span className="text-sm">{folder.name}</span>
                </div>
              ))}
              {folders.length === 0 && <p className="text-sm text-gray-500">No folders</p>}
              <Button size="sm" variant="outline" className="w-full mt-4">
                <Folder className="w-4 h-4 mr-2" />New Folder
              </Button>
            </div>
          </div>
        </Card>

        <Card className="md:col-span-3">
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Documents</h2>
            <div className="grid gap-3">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-2xl">{getCategoryIcon(doc.category)}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{doc.name}</h3>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">v{doc.version}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {doc.category} | {(doc.file_size / 1024).toFixed(0)}KB | {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                      {doc.tags && doc.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {doc.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteDocument(doc.id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {documents.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <File className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => { fetchData(); setShowUploadModal(false); }}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
};

const UploadDocumentModal = ({ onClose, onSuccess, getAuthHeader }) => {
  const [file, setFile] = useState(null);
  const [metadata, setMetadata] = useState({
    name: '',
    category: 'other',
    version: '1.0',
    tags: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify({
        ...metadata,
        tags: metadata.tags.split(',').map(t => t.trim()).filter(Boolean)
      }));

      await axios.post(`${BACKEND_URL}/api/erp/documents/upload`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      onSuccess();
    } catch (error) {
      alert('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">Upload Document</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Document Name *</label>
            <input
              required
              value={metadata.name}
              onChange={e => setMetadata({...metadata, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category *</label>
            <select
              required
              value={metadata.category}
              onChange={e => setMetadata({...metadata, category: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="contract">Contract</option>
              <option value="drawing">Drawing</option>
              <option value="invoice">Invoice</option>
              <option value="report">Report</option>
              <option value="certificate">Certificate</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
            <input
              value={metadata.tags}
              onChange={e => setMetadata({...metadata, tags: e.target.value})}
              placeholder="e.g., structural, approved, final"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">File * (Max 50MB)</label>
            <input
              type="file"
              required
              onChange={e => setFile(e.target.files[0])}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-primary" disabled={loading}>
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ERPDocuments;
